import { z, type ZodTypeAny } from 'zod';
import { QuestionType, ValidationConfig } from '../../types';

/**
 * Builds a zod schema for a single survey question's `value` field.
 * Checkbox / multiselect use number[] (option ids); other types use string.
 *
 * @param questionType - Survey question input type
 * @param validationConfiguration - Per-question validation flags
 * @returns Zod object with a `value` field
 */
export function generateQuestionResponseSchema(
  questionType: QuestionType,
  validationConfiguration: ValidationConfig
): z.ZodObject<{ value: ZodTypeAny }> {
  const isRequired = validationConfiguration?.isRequired || false;

  let valueSchema: ZodTypeAny;

  switch (questionType) {
    case 'checkbox':
    case 'multiselect': {
      const arraySchema = z.array(z.number());
      valueSchema = isRequired
        ? arraySchema.min(1)
        : arraySchema.optional().nullish();
      break;
    }
    case 'date':
    case 'text':
    case 'textarea':
    default: {
      const stringSchema = z.string();
      valueSchema = isRequired
        ? stringSchema
        : stringSchema.optional().nullish();
      break;
    }
  }

  return z.object({
    value: valueSchema,
  });
}
